import {getFormattedDate} from '@utilities/common';
import React from 'react';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import Select from '@components/Select';
import { PaymentTypes } from '@constants/common';
type ActionsRendererOptions = {
    data?: { orderId?: string };
    onEdit?: (order: unknown) => void;
    onPrintFoodBill?: (order: unknown) => void;
    onPrintRoomPdf?: (order: unknown) => void;
    showEdit?: boolean;
    showPrintFoodBill?: boolean;
    showPrintRoomPdf?: boolean;
};

// Custom cell renderer for Actions (Edit / Print food bill / Print room PDF)
const ActionsRenderer = (props: ActionsRendererOptions) => {
    const {
        data,
        onEdit,
        onPrintFoodBill,
        onPrintRoomPdf,
        showEdit = false,
        showPrintFoodBill = false,
        showPrintRoomPdf = false,
    } = props;

    if (data?.orderId === 'Total:') {
        return React.createElement('span', null, '');
    }

    const buttons: React.ReactElement[] = [];

    if (showEdit && onEdit) {
        buttons.push(
            React.createElement(IconButton, {
                key: 'edit',
                size: 'small',
                onClick: () => onEdit(data),
                sx: { mr: 0.25 },
                title: 'Edit',
                'aria-label': 'Edit',
            },
                React.createElement(EditIcon, { fontSize: 'small' }),
            ),
        );
    }

    if (showPrintFoodBill && onPrintFoodBill) {
        buttons.push(
            React.createElement(IconButton, {
                key: 'bill',
                size: 'small',
                onClick: () => onPrintFoodBill(data),
                sx: { mr: 0.25 },
                title: 'Print bill',
                'aria-label': 'Print bill',
            },
                React.createElement(PrintIcon, { fontSize: 'small' }),
            ),
        );
    }

    if (showPrintRoomPdf && onPrintRoomPdf) {
        buttons.push(
            React.createElement(IconButton, {
                key: 'pdf',
                size: 'small',
                onClick: () => onPrintRoomPdf(data),
                sx: { mr: 0.25 },
                title: 'Print room PDF',
                'aria-label': 'Print room PDF',
            },
                React.createElement(PrintIcon, { fontSize: 'small' }),
            ),
        );
    }

    if (!buttons.length) {
        return React.createElement('span', null, '');
    }

    return React.createElement(
        'div',
        { style: { display: 'flex', alignItems: 'center', flexWrap: 'nowrap' } },
        ...buttons
    );
};

// Custom cell renderer for View IDs link
const ViewIdsRenderer = (props: any) => {
    const { data, onViewIds } = props;
    const files = data?.identificationFiles;

    if (data?.orderId === 'Total:' || !files?.length) {
        return React.createElement('span', { style: { color: '#999', fontSize: '12px' } }, '—');
    }

    return React.createElement('a', {
        onClick: () => onViewIds(files),
        style: { color: '#1976d2', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' },
    }, `View Ids (${files.length})`);
};

type PaymentModeRendererOptions = {
    data?: { orderId?: string; selectedPayment?: string };
    onPaymentChange?: (orderId: string, paymentMode: string) => void;
};

const stopGridEvent = (e: React.SyntheticEvent) => {
    e.stopPropagation();
};

const PaymentModeRenderer = (props: PaymentModeRendererOptions) => {
    const { data, onPaymentChange } = props;
    const payment = data?.selectedPayment || '';

    if (data?.orderId === 'Total:' || !data?.orderId) {
        return React.createElement('span', null, '');
    }

    const selectedOption = payment
        ? { label: payment, value: payment }
        : null;

    return React.createElement(
        'div',
        {
            className: 'reportPaymentSelectWrap',
            onClick: stopGridEvent,
            onMouseDown: stopGridEvent,
            onTouchStart: stopGridEvent,
        },
        React.createElement(Select, {
            className: 'selectWrap reportPaymentSelect',
            value: selectedOption,
            options: PaymentTypes,
            menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
            menuPosition: 'fixed',
            menuPlacement: 'auto',
            onChange: (val: { value?: string }) => onPaymentChange?.(data.orderId!, val?.value || ''),
            onMouseDown: stopGridEvent,
            onTouchStart: stopGridEvent,
            styles: {
                menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
                control: (base: Record<string, unknown>) => ({
                    ...base,
                    minHeight: 28,
                    height: 28,
                    fontSize: '0.75rem',
                }),
                valueContainer: (base: Record<string, unknown>) => ({
                    ...base,
                    padding: '0 6px',
                    height: 26,
                }),
                indicatorsContainer: (base: Record<string, unknown>) => ({
                    ...base,
                    height: 26,
                }),
                dropdownIndicator: (base: Record<string, unknown>) => ({
                    ...base,
                    padding: '2px 4px',
                }),
            },
        }),
    );
};

// Column Definitions: Defines the columns to be displayed.
export const colDefsOrderBills:any = [
    {headerName: 'Order Id', valueGetter: (p: any) => p?.data?.orderSeqNo || p?.data?.uoId, width: 200, suppressMovable: true},
    {
        headerName: 'Actions',
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
            onEdit: null,
        },
        width: 110,
        suppressMovable: true,
    },
    {headerName: 'Table#', valueGetter: (p: any) => p?.data?.tableNumber, width: 130, suppressMovable: true},
    {headerName: 'Total Cost', valueGetter: (p: any) => p?.data?.totalCost, width: 115, suppressMovable: true},
    {headerName: 'Items', valueGetter: (p: any) => p?.data?.itemsCost, width: 100, suppressMovable: true},
    {headerName: 'Discount', valueGetter: (p: any) => p?.data?.discount, width: 110, suppressMovable: true},
    {headerName: 'GST', valueGetter: (p: any) => p?.data?.gstBill, width: 100, suppressMovable: true},
    {
        headerName: 'Type',
        cellRenderer: PaymentModeRenderer,
        cellClass: 'ag-cell-payment-select',
        cellRendererParams: {
            onPaymentChange: null,
        },
        width: 130,
        suppressMovable: true,
    },
    {headerName: 'Bill Date', valueGetter: (p: any) => getFormattedDate(p?.data?.createdAt), width: 185, suppressMovable: true},
    {headerName: 'UUID', valueGetter: (p: any) => p?.data?.orderId, width: 170, suppressMovable: true}
];

export const colDefsRoomBills:any = [
    {headerName: 'Order Id', valueGetter: (p: any) => p?.data?.orderSeqNo || p?.data?.uoId || p?.data?.orderId, width: 200, suppressMovable: true},
    {
        headerName: 'View Ids',
        cellRenderer: ViewIdsRenderer,
        cellRendererParams: {
            onViewIds: null, // This will be set dynamically
        },
        width: 130,
        suppressMovable: true,
    },
    {
        headerName: 'Actions',
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
            onEdit: null, // This will be set dynamically
        },
        width: 130,
        suppressMovable: true,
    },
    {headerName: 'Guest Name', valueGetter: (p: any) => p?.data?.guestName, width: 130, suppressMovable: true},
    {headerName: 'Room#', valueGetter: (p: any) => {
        let sum = "";
        p?.data?.rooms?.forEach((room: any) => {
            sum += room?.roomNumber + ", ";
        });
        return sum;
    }, width: 130, suppressMovable: true},
    {headerName: 'Total Cost', valueGetter: (p: any) => p?.data?.totalCost || 0, width: 115, suppressMovable: true},
    {headerName: 'Items', valueGetter: (p: any) => p?.data?.itemsCost || 0, width: 100, suppressMovable: true},
    {headerName: 'Discount', valueGetter: (p: any) => p?.data?.discount || 0, width: 110, suppressMovable: true},
    {headerName: 'GST', valueGetter: (p: any) => p?.data?.gstBill || 0, width: 100, suppressMovable: true},
    {
        headerName: 'Payment Mode',
        cellRenderer: PaymentModeRenderer,
        cellClass: 'ag-cell-payment-select',
        cellRendererParams: {
            onPaymentChange: null,
        },
        width: 130,
        suppressMovable: true,
    },
    {headerName: 'Check-In', valueGetter: (p: any) => getFormattedDate(p?.data?.checkInDate), width: 185, suppressMovable: true},
    {headerName: 'Check-Out', valueGetter: (p: any) => getFormattedDate(p?.data?.checkOutDate), width: 185, suppressMovable: true},
    {headerName: 'Bill Date', valueGetter: (p: any) => getFormattedDate(p?.data?.createdAt), width: 185, suppressMovable: true},
    {headerName: 'Updated At', valueGetter: (p: any) => getFormattedDate(p?.data?.updatedAt), width: 185, suppressMovable: true},
    {headerName: 'UUID', valueGetter: (p: any) => p?.data?.orderId, width: 110, suppressMovable: true}
];

// Function to update column definitions with super admin state
export const getUpdatedColDefs = (
    isSuperAdmin: boolean,
    orderType: Boolean,
    onToggle: (orderId: string, newValue: boolean) => void,
    onViewIds?: (images: string[]) => void,
    onEditRoomBill?: (order: any) => void,
    onEditFoodOrder?: (order: any) => void,
    onPrintFoodBill?: (order: any) => void,
    onPrintRoomPdf?: (order: any) => void,
    onPaymentChange?: (orderId: string, paymentMode: string) => void,
) => {
    const updateColDefs = (
        colDefs: any[],
        onEdit?: (order: any) => void,
        options?: {
            showEdit?: boolean;
            showPrintFoodBill?: boolean;
            onPrintFoodBill?: (order: any) => void;
            showPrintRoomPdf?: boolean;
            onPrintRoomPdf?: (order: any) => void;
        }
    ) => {
        const showEdit = options?.showEdit === true;
        return colDefs.map(colDef => {
            if (colDef.headerName === 'GST') {
                return {
                    ...colDef,
                    cellRendererParams: {
                        isSuperAdmin,
                        orderType: orderType ? 'room-bills' : 'orders',
                        onToggle
                    }
                };
            }
            if (colDef.headerName === 'View Ids') {
                return {
                    ...colDef,
                    cellRendererParams: {
                        onViewIds: onViewIds || null,
                    }
                };
            }
            if (colDef.headerName === 'Actions') {
                return {
                    ...colDef,
                    cellRendererParams: {
                        onEdit: onEdit || null,
                        showEdit,
                        showPrintFoodBill: !!options?.showPrintFoodBill,
                        onPrintFoodBill: options?.onPrintFoodBill || null,
                        showPrintRoomPdf: !!options?.showPrintRoomPdf,
                        onPrintRoomPdf: options?.onPrintRoomPdf || null,
                    }
                };
            }
            if (colDef.headerName === 'Type' || colDef.headerName === 'Payment Mode') {
                return {
                    ...colDef,
                    cellRendererParams: {
                        onPaymentChange: onPaymentChange || null,
                    }
                };
            }
            return colDef;
        });
    };

    return {
        orderBills: updateColDefs(colDefsOrderBills, isSuperAdmin ? onEditFoodOrder : undefined, {
            showEdit: isSuperAdmin,
            showPrintFoodBill: true,
            onPrintFoodBill: onPrintFoodBill,
        }),
        roomBills: updateColDefs(colDefsRoomBills, isSuperAdmin ? onEditRoomBill : undefined, {
            showEdit: isSuperAdmin,
            showPrintRoomPdf: true,
            onPrintRoomPdf: onPrintRoomPdf,
        }),
    };
};
